import { useQuery } from '@tanstack/react-query';
import { QuizResult } from '../types';

const { REACT_APP_GRAPHQL_APIURL } = process.env;

const TIMEOUT_MS = 30 * 1000;
const TWO_WEEKS_MS = 14 * 24 * 60 * 60 * 1000;
const FIVE_DAYS_MS = 5 * 24 * 60 * 60 * 1000;
const FIVE_MINUTES_MS = 5 * 60 * 1000;

const queryResults = `query Results($quizId: String!) {
  quizResult(quizId: $quizId) {
    createdAt
    email
    name
    quizId
    score
    userId
  }
}`;

async function fetchWithTimeout(
  url: string,
  options: RequestInit,
  timeoutMs: number = TIMEOUT_MS
): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    return response;
  } finally {
    clearTimeout(timeoutId);
  }
}

export async function fetchQuizResults(quizId: string): Promise<QuizResult[]> {
  const response = await fetchWithTimeout(REACT_APP_GRAPHQL_APIURL as string, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      query: queryResults,
      variables: { quizId },
    }),
  });

  if (!response.ok) {
    throw new Error(`Get quiz results failed! (${response.status})`);
  }

  const data = await response.json();
  return (data?.data?.quizResult || []) as QuizResult[];
}

export function useQuizResults(
  quizId: string | null,
  options?: { hasEnded?: boolean; enabled?: boolean }
) {
  const hasEnded = options?.hasEnded ?? false;

  return useQuery({
    queryKey: ['quiz', 'results', quizId],
    queryFn: () => fetchQuizResults(quizId!),
    enabled: !!quizId && (options?.enabled ?? true),
    staleTime: hasEnded ? TWO_WEEKS_MS : 0,
    gcTime: hasEnded ? FIVE_DAYS_MS : FIVE_MINUTES_MS,
    retry: false,
    refetchOnWindowFocus: !hasEnded,
    refetchOnMount: !hasEnded,
  });
}
